import {parse, tokenizer} from 'acorn';
import {KEYS} from '../../../translation-keys';
import {TemplateVariable} from '../../froala/template-variables';

/** Describes an editable source range without serializing the surrounding JavaScript. */
export interface FormulaPart {
  kind: 'variable' | 'function' | 'condition' | 'constant';
  start: number;
  end: number;
  from: number;
  to: number;
  name: string;
  fields?: string[];
  statement?: boolean;
}

/** Describes a supported Math function and its initial argument count. */
export interface FormulaFunction {name: string; label: string; args: number; variadic?: boolean;}

/** Describes why an expression must stay in the lossless source view. */
export interface FormulaInspection {
  reason: '' | 'syntax' | 'complexity' | 'construct' | 'runtime';
  parts: FormulaPart[];
}

/** Lists runtime-compatible Math functions; log1p is rejected by the mobile call allowlist. */
export const FORMULA_FUNCTIONS: FormulaFunction[] = [
  {name: 'round', label: KEYS.EDITOR.FORMULA_FN_ROUND, args: 1},
  {name: 'ceil', label: KEYS.EDITOR.FORMULA_FN_CEIL, args: 1},
  {name: 'floor', label: KEYS.EDITOR.FORMULA_FN_FLOOR, args: 1},
  {name: 'trunc', label: KEYS.EDITOR.FORMULA_FN_TRUNC, args: 1},
  {name: 'abs', label: KEYS.EDITOR.FORMULA_FN_ABS, args: 1},
  {name: 'min', label: KEYS.EDITOR.FORMULA_FN_MIN, args: 2, variadic: true},
  {name: 'max', label: KEYS.EDITOR.FORMULA_FN_MAX, args: 2, variadic: true},
  {name: 'pow', label: KEYS.EDITOR.FORMULA_FN_POW, args: 2},
  {name: 'sqrt', label: KEYS.EDITOR.FORMULA_FN_SQRT, args: 1},
  {name: 'cbrt', label: KEYS.EDITOR.FORMULA_FN_CBRT, args: 1},
  {name: 'hypot', label: KEYS.EDITOR.FORMULA_FN_HYPOT, args: 2, variadic: true},
  {name: 'sign', label: KEYS.EDITOR.FORMULA_FN_SIGN, args: 1},
  {name: 'random', label: KEYS.EDITOR.FORMULA_FN_RANDOM, args: 0},
  ...['exp', 'expm1', 'log', 'log2', 'log10', 'sin', 'cos', 'tan', 'asin', 'acos', 'atan',
    'sinh', 'cosh', 'tanh', 'asinh', 'acosh', 'atanh', 'fround', 'clz32'].map(name => ({name, label: `Math.${name}`, args: 1})),
  {name: 'atan2', label: 'Math.atan2', args: 2}, {name: 'imul', label: 'Math.imul', args: 2}
];

/** Keeps legacy parenthesized if statements readable without changing their stored source. */
export function formulaSource(source: string): {code: string; offset: number} {
  if (source.startsWith('(if')) {
    return {code: source.slice(1).replace(/\)$/, ''), offset: 1};
  }
  return {code: source, offset: 0};
}

/** Parses scripts without compiling or executing authored code. */
export function parseFormula(source: string): any {
  return parse(formulaSource(source).code, {ecmaVersion: 'latest', preserveParens: true});
}

/** Visits syntax nodes with their immediate parent. */
export function visitFormula(node: any, visitor: (node: any, parent?: any) => void, parent?: any): void {
  const pending = [{node, parent}];
  while (pending.length) {
    const entry = pending.pop();
    if (!entry.node || typeof entry.node.type !== 'string') { continue; }
    visitor(entry.node, entry.parent);
    Object.keys(entry.node).reverse().forEach(key => {
      const value = entry.node[key];
      if (Array.isArray(value)) {
        value.slice().reverse().forEach(child => pending.push({node: child, parent: entry.node}));
      } else if (value && typeof value === 'object') { pending.push({node: value, parent: entry.node}); }
    });
  }
}

/** Resolves only static member paths, including quoted patient custom field names. */
export function formulaPath(node: any): string {
  const keys: string[] = [];
  while (node?.type === 'MemberExpression') {
    const key = node.computed ? node.property.type === 'Literal' && node.property.value : node.property.name;
    if (typeof key !== 'string' || keys.length > 64) { return ''; }
    keys.unshift(key);
    node = node.object;
  }
  return node?.type === 'Identifier' ? [node.name, ...keys].join('.') : '';
}

/** Mirrors the mobile function-call filter, including its whitespace and naming quirks. */
export function formulaRejectedCall(source: string): string {
  const calls = formulaSource(source).code.match(/([a-zA-Z\. ]+\()/g) || [];
  return calls.map(call => call.replace(/^\s+/, '')).find(call => !/^if\s?\(/.test(call) &&
    !/^Math\.[a-zA-Z ]+\(/.test(call) && !/^[a-zA-Z\. ]+\.match\(/.test(call) && !/^\(/.test(call)) || '';
}

/** Requires a bounded, explicitly understood syntax tree before enabling visual edits. */
export function inspectFormula(source: string): FormulaInspection {
  if (!source.trim()) { return {reason: '', parts: []}; }
  if (source.length > 20000) { return {reason: 'complexity', parts: []}; }
  const allowed = new Set(['Program', 'ExpressionStatement', 'IfStatement', 'BlockStatement', 'ConditionalExpression',
    'BinaryExpression', 'LogicalExpression', 'UnaryExpression', 'MemberExpression', 'Identifier', 'Literal',
    'CallExpression', 'ParenthesizedExpression', 'ArrayExpression', 'EmptyStatement']);
  try {
    const tree = parseFormula(source);
    let reason: FormulaInspection['reason'] = formulaRejectedCall(source) ? 'runtime' : '';
    let count = 0;
    const depths = new Map<any, number>();
    visitFormula(tree, (node, parent) => {
      const depth = (depths.get(parent) || 0) + 1;
      depths.set(node, depth);
      if (++count > 1500 || depth > 64) { reason = 'complexity'; return; }
      if (!allowed.has(node.type) || node.optional) { reason = reason || 'construct'; }
      if (node.type === 'CallExpression') {
        const path = formulaPath(node.callee);
        const supported = FORMULA_FUNCTIONS.some(item => path === `Math.${item.name}`) ||
          /^(inline|extern|client|patient)\..+\.match$/.test(path);
        if (!supported) { reason = reason || 'construct'; }
      }
    });
    if (reason) { return {reason, parts: []}; }
    return {reason: '', parts: formulaParts(source)};
  } catch (_) { return {reason: 'syntax', parts: []}; }
}

/** Creates safe JavaScript member access for a catalog path. */
export function formulaVariableCode(path: string): string {
  return path.split('.').map((part, index) => index === 0 ? part :
    /^[a-zA-Z_$][\w$]*$/.test(part) ? `.${part}` : `[${JSON.stringify(part)}]`).join('');
}

/** Keeps every question and answer level identifiable in a compact inline token. */
export function formulaVariableLabel(variable: TemplateVariable): string {
  const context = variable.questionContext;
  const levels = !context ? [variable.label] : !context.ancestors.length && context.questionTitle === context.label ?
    [context.label] : [context.questionTitle, ...context.ancestors, context.label];
  return levels.map(level => Array.from(level).length > 25 ? Array.from(level).slice(0, 22).join('') + '...' : level).join(' › ');
}

/** Reads semantic decorations and editable ranges while leaving all original bytes intact. */
export function formulaParts(source: string): FormulaPart[] {
  const {code, offset} = formulaSource(source);
  let tree: any;
  try { tree = parseFormula(source); } catch (_) { return []; }
  const parts: FormulaPart[] = [];
  const tokens = Array.from(tokenizer(code, {ecmaVersion: 'latest'})) as any[];
  /** Extracts the body of a branch, retaining comments and nested statements. */
  const branch = (node: any): string => node ? code.slice(node.start + (node.type === 'BlockStatement' ? 1 : 0),
    node.end - (node.type === 'BlockStatement' ? 1 : 0)).trim() : '';
  /** Adds a decoration using source-relative offsets. */
  const add = (kind: FormulaPart['kind'], start: number, end: number, node: any, name: string,
    fields?: string[], statement = false): void => {
    parts.push({kind, start: start + offset, end: end + offset, from: node.start + offset, to: node.end + offset,
      name, fields, statement});
  };
  visitFormula(tree, (node, parent) => {
    if (node.type === 'Identifier' && ['date', 'time'].indexOf(node.name) !== -1 &&
      !(parent?.type === 'MemberExpression' && parent.property === node && !parent.computed)) {
      add('variable', node.start, node.end, node, node.name);
    }
    if (node.type === 'CallExpression') {
      const path = formulaPath(node.callee);
      const math = FORMULA_FUNCTIONS.find(item => path === `Math.${item.name}`);
      if (math) {
        add('function', node.callee.start, node.callee.end, node, path, node.arguments.map(arg => code.slice(arg.start, arg.end)));
      } else if (node.callee.type === 'MemberExpression' && path.endsWith('.match')) {
        add('function', node.callee.property.start, node.callee.property.end, node, 'match',
          [code.slice(node.callee.object.start, node.callee.object.end), ...node.arguments.map(arg => code.slice(arg.start, arg.end))]);
      }
    }
    if (node.type === 'MemberExpression' && !(parent?.type === 'MemberExpression' && parent.object === node &&
      !formulaPath(parent).endsWith('.match'))) {
      const path = formulaPath(node);
      if (/^(inline|extern|scoring|formula|client|patient|container)\./.test(path) && !path.endsWith('.match')) {
        add('variable', node.start, node.end, node, path);
      } else if (/^Math\.[A-Z0-9_]+$/.test(path)) {
        add('constant', node.start, node.end, node, path);
      }
    }
    if (node.type === 'IfStatement') {
      const fields = [code.slice(node.test.start, node.test.end), branch(node.consequent), branch(node.alternate)];
      add('condition', node.start, node.start + 2, node, 'Wenn', fields, true);
      if (node.consequent.type === 'BlockStatement') {
        add('condition', node.consequent.start, node.consequent.start + 1, node, 'Dann', fields, true);
      }
      if (node.alternate) {
        const otherwise = tokens.find(token => token.start >= node.consequent.end && token.end <= node.alternate.start &&
          token.type.label === 'else');
        if (otherwise) { add('condition', otherwise.start, otherwise.end, node, 'Sonst', fields, true); }
      }
    }
    if (node.type === 'ConditionalExpression') {
      const question = tokens.find(token => token.start >= node.test.end && token.end <= node.consequent.start && token.type.label === '?');
      const colon = tokens.find(token => token.start >= node.consequent.end && token.end <= node.alternate.start && token.type.label === ':');
      const fields = [node.test, node.consequent, node.alternate].map(item => code.slice(item.start, item.end));
      add('condition', node.start, node.start, node, 'Wenn', fields);
      if (question) { add('condition', question.start, question.end, node, 'Dann', fields); }
      if (colon) { add('condition', colon.start, colon.end, node, 'Sonst', fields); }
    }
  });
  return parts;
}

/** Produces a new fragment only after an explicit dialog confirmation. */
export function writeFormulaPart(part: FormulaPart): string {
  const fields = part.fields || [];
  if (part.kind === 'condition') {
    return part.statement ? `if (${formulaOperand(fields[0])}) {\n${fields[1]}\n}${fields[2]?.trim() ? ` else {\n${fields[2]}\n}` : ''}` :
      `(${formulaOperand(fields[0])} ? ${formulaOperand(fields[1])} : ${formulaOperand(fields[2])})`;
  }
  return part.name === 'match' ? `${fields[0]}.match(${fields.slice(1).map(formulaOperand).join(', ')})` :
    `${part.name}(${fields.map(formulaOperand).join(', ')})`;
}

/** Keeps each operand a single expression and preserves precedence and trailing line comments. */
function formulaOperand(source: string): string {
  const tree: any = parse(`(${source}\n)`, {ecmaVersion: 'latest', preserveParens: true});
  const expression = tree.body[0].expression.expression;
  if (['ConditionalExpression', 'SequenceExpression', 'AssignmentExpression'].indexOf(expression.type) !== -1) {
    return `(${source}\n)`;
  }
  let trailingComment = false;
  Array.from(tokenizer(source, {ecmaVersion: 'latest', onComment: (block, _text, _start, end) => {
    if (!block && !source.slice(end).trim()) { trailingComment = true; }
  }}));
  return source + (trailingComment ? '\n' : '');
}
